import type {
  ActionStruct,
  AppProfile,
  AppSpec,
  AppStruct,
  ConnectorSpec,
  ConnectorStruct,
  PerkIntegrationSpec,
} from '@cohort/shared/schema/apps/app';
import {AppProfileSchema, AppProfileWithIdSchema} from '@cohort/shared/schema/apps/app';
import {DiscordPrivateAccessPerkIntegrationSpec} from '@cohort/shared/schema/apps/discord/perks/privateAccess';
import type {OAuth2Credentials} from '@cohort/shared/schema/apps/oauth';
import {
  OAuth2AppCredentialsSchema,
  OAuth2CredentialsSchema,
} from '@cohort/shared/schema/apps/oauth';
import {z} from 'zod';

export const DiscordAppCredentialsSchema = OAuth2AppCredentialsSchema.extend({
  botToken: z.string(),
});
export type DiscordAppCredentials = z.infer<typeof DiscordAppCredentialsSchema>;

export const DiscordAppProfileSchema = AppProfileWithIdSchema.extend({
  username: z.string(),
  discriminator: z.string(),
});
export type DiscordAppProfile = z.infer<typeof DiscordAppProfileSchema>;

export type DiscordMerchantConnectorStruct = ConnectorStruct<
  'discord-merchant',
  DiscordAppCredentials,
  DiscordMerchantCredentials,
  AppProfile,
  null
>;

export type DiscordUserConnectorStruct = ConnectorStruct<
  'discord',
  DiscordAppCredentials,
  OAuth2Credentials,
  DiscordAppProfile,
  null
>;

// Actions
// Create Role
export const CreateRoleInputSchema = z.object({
  name: z.string(),
});
export type CreateRoleInput = z.infer<typeof CreateRoleInputSchema>;

export const CreateRoleOutputSchema = z.object({
  roleId: z.string(),
});
export type CreateRoleOutput = z.infer<typeof CreateRoleOutputSchema>;

export type CreateRoleActionStruct = ActionStruct<'create-role', CreateRoleInput, CreateRoleOutput>;

// Invite to Channel
export const InviteToChannelInputSchema = z.object({
  discordUserId: z.string(),
  roleId: z.string(),
});
export type InviteToChannelInput = z.infer<typeof InviteToChannelInputSchema>;

export const InviteToChannelOutputSchema = z.object({
  inviteUrl: z.string().url(),
  guildName: z.string(),
});
export type InviteToChannelOutput = z.infer<typeof InviteToChannelOutputSchema>;

export type InviteToChannelActionStruct = ActionStruct<
  'invite-to-channel',
  InviteToChannelInput,
  InviteToChannelOutput
>;

// Remove Role
export const RemoveRoleInputSchema = z.object({
  discordUserId: z.string(),
  roleId: z.string(),
});
export type RemoveRoleInput = z.infer<typeof RemoveRoleInputSchema>;

export const RemoveRoleOutputSchema = z.object({});
export type RemoveRoleOutput = z.infer<typeof RemoveRoleOutputSchema>;

export type RemoveRoleActionStruct = ActionStruct<'remove-role', RemoveRoleInput, RemoveRoleOutput>;

export const DiscordUserConnectorSpec = {
  id: 'discord',
  type: 'oauth2',
  appCredentialsSchema: DiscordAppCredentialsSchema,
  credentialsSchema: OAuth2CredentialsSchema,
  profileSchema: DiscordAppProfileSchema,
  connectionConfigSchema: null,
} satisfies ConnectorSpec<DiscordUserConnectorStruct>;

export const DiscordMerchantCredentialsSchema = z.object({
  guildId: z.string(),
});
export type DiscordMerchantCredentials = z.infer<typeof DiscordMerchantCredentialsSchema>;

// This should be oauth2, just do it simply for now
export const DiscordMerchantConnectorSpec = {
  id: 'discord-merchant',
  type: 'custom',
  appCredentialsSchema: DiscordAppCredentialsSchema,
  credentialsSchema: DiscordMerchantCredentialsSchema,
  profileSchema: AppProfileSchema,
  connectionConfigSchema: null,
} satisfies ConnectorSpec<DiscordMerchantConnectorStruct>;

export const DiscordPerkIntegrationSpecs = [
  DiscordPrivateAccessPerkIntegrationSpec,
] as const satisfies ReadonlyArray<PerkIntegrationSpec>;
type DiscordPerkIntegrationIds = (typeof DiscordPerkIntegrationSpecs)[number]['id'];

export type DiscordAppStruct = AppStruct<
  'discord',
  DiscordMerchantConnectorStruct,
  DiscordUserConnectorStruct,
  null,
  never,
  never,
  DiscordPerkIntegrationIds,
  never,
  never
>;
export const DiscordAppSpec: AppSpec<DiscordAppStruct> = {
  id: 'discord',
  name: 'Discord',
  merchantConnector: DiscordMerchantConnectorSpec,
  userConnector: DiscordUserConnectorSpec,
  actionSpecs: [],
  usersSyncSpec: null,
  notificationIntegrationSpecs: [],
  perkIntegrationSpecs: DiscordPerkIntegrationSpecs,
  triggerIntegrationSpecs: [],
  userEventSpecs: [],
};
